import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Layers, FileQuestion, TrendingUp } from 'lucide-react';
import { subjectsApi, topicsApi, questionsApi } from '@/services/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    subjects: 0,
    topics: 0,
    questions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentSubjects, setRecentSubjects] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const subjectsRes = await subjectsApi.getAll();
      const subjects = subjectsRes.data || [];
      setRecentSubjects(subjects.slice(0, 5));

      let totalTopics = 0;
      let totalQuestions = 0;

      for (const subject of subjects) {
        try {
          const topicsRes = await topicsApi.getBySubject(subject._id);
          const topics = topicsRes.data || [];
          totalTopics += topics.length;

          for (const topic of topics) {
            try {
              const questionsRes = await questionsApi.getByTopic(topic._id);
              totalQuestions += (questionsRes.data || []).length;
            } catch (e) {
              console.error('Questions fetch error:', e);
            }
          }
        } catch (e) {
          console.error('Topics fetch error:', e);
        }
      }

      setStats({
        subjects: subjects.length,
        topics: totalTopics,
        questions: totalQuestions,
      });
    } catch (error) {
      console.error('Stats loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Toplam Ders', value: stats.subjects, icon: BookOpen, color: 'text-teal-600 bg-teal-50', link: '/subjects' },
    { title: 'Toplam Konu', value: stats.topics, icon: Layers, color: 'text-blue-600 bg-blue-50', link: '/topics' },
    { title: 'Toplam Soru', value: stats.questions, icon: FileQuestion, color: 'text-purple-600 bg-purple-50', link: '/questions' },
  ];

  return (
    <Layout breadcrumbs={[{ label: 'Gösterge Paneli' }]}>
      <div className="space-y-8" data-testid="dashboard">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gösterge Paneli
          </h1>
          <p className="text-slate-500 mt-1">Eğitim içeriğinizi yönetin</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <Card className="hover:border-teal-200 transition-colors cursor-pointer" data-testid={`stat-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {loading ? '-' : stat.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.color}`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Subjects */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Son Eklenen Dersler
            </CardTitle>
            <Link to="/subjects" className="text-sm text-teal-600 hover:text-teal-700">
              Tümünü Gör →
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-500">Yükleniyor...</p>
            ) : recentSubjects.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="text-slate-500 mt-2">Henüz ders eklenmemiş</p>
                <Link to="/subjects" className="text-teal-600 hover:underline text-sm">
                  İlk dersi ekle
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentSubjects.map((subject) => (
                  <Link
                    key={subject._id}
                    to={`/subjects/${subject._id}/topics`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                    data-testid={`recent-subject-${subject._id}`}
                  >
                    <span className="text-2xl">{subject.icon || '📚'}</span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{subject.name}</p>
                      <p className="text-sm text-slate-500">
                        {subject.groups?.length > 0 
                          ? `${subject.groups.join(', ')}. sınıflar`
                          : 'Grup belirtilmemiş'
                        }
                      </p>
                    </div>
                    <TrendingUp className="w-4 h-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
